import { motion } from 'framer-motion';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { ApiService } from '../../../services/api';
import { useLanguageMode, bilingual } from '../../../i18n/bilingual';

export default function ForgotPage() {
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const { mode } = useLanguageMode();

  const handleSend = async () => {
    setStatus(null);
    try {
      setIsSending(true);
      const res = await ApiService.forgotPassword(email);
      setStatus(res?.message || bilingual(mode, 'Wenn die E‑Mail existiert, wurde ein Link zum Zurücksetzen gesendet.', 'If the email exists, a reset link was sent.'));
    } catch (err: any) {
      setStatus(err?.response?.data?.error || err?.message || bilingual(mode, 'Senden der Zurücksetzungs‑E‑Mail fehlgeschlagen.', 'Failed to send reset email.'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main className="pt-16 sm:pt-20 pb-10 sm:pb-20">
        <div className="max-w-sm sm:max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gray-900 rounded-xl sm:rounded-2xl border border-gray-700 p-4 sm:p-6 lg:p-8 mt-8 sm:mt-20"
          >
            <div className="text-center mb-6 sm:mb-8">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
              >
                <i className="ri-key-2-line text-lg sm:text-2xl text-black"></i>
              </motion.div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2">{bilingual(mode, 'Passwort vergessen', 'Forgot your password')}</h1>
              <p className="text-gray-400 text-sm sm:text-base">{bilingual(mode, 'Gib deine E‑Mail ein, um einen Link zum Zurücksetzen zu erhalten', 'Enter your email to receive a reset link')}</p>
            </div>

            <div className="mt-2 sm:mt-4">
              <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                {bilingual(mode, 'E‑Mail‑Adresse', 'Email address')}
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-gray-800 border border-gray-600 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors duration-200 text-sm sm:text-base"
                  placeholder={bilingual(mode, 'Deine E‑Mail', 'Your email')}
                />
                <Button
                  type="button"
                  disabled={isSending || !email}
                  className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:from-yellow-500 hover:to-yellow-700 whitespace-nowrap cursor-pointer text-sm sm:text-base"
                  onClick={handleSend}
                >
                  {isSending ? bilingual(mode, 'Wird gesendet...', 'Sending…') : bilingual(mode, 'Link senden', 'Send link')}
                </Button>
              </div>
              {status && <p className="text-xs sm:text-sm text-gray-300 mt-2">{status}</p>}
            </div>

            <div className="mt-6 sm:mt-8 text-center">
              <p className="text-gray-400 text-xs sm:text-sm">
                {bilingual(mode, 'Passwort wieder eingefallen?', 'Remembered your password?')}{' '}
                <Link to="/signin" className="text-yellow-400 hover:text-yellow-300 transition-colors font-medium">
                  {bilingual(mode, 'Hier anmelden', 'Sign in here')}
                </Link>
              </p>
+             <p className="text-gray-500 text-xs sm:text-sm mt-2">
+               {bilingual(mode, 'Mit der Nutzung stimmst du unseren ', 'By using this feature, you agree to our ')}
+               <Link to="/terms" className="text-yellow-400 hover:text-yellow-300 transition-colors font-medium">
+                 {bilingual(mode, 'Nutzungsbedingungen', 'Terms of Service')}
+               </Link>
+               {bilingual(mode, ' zu.', '.')}
+             </p>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}